/** Where the back arrow leads. The rule: back to the screen you came from, when that was another screen of the app;
 *  with none (a reload, a shared link, a new tab) the caller falls back to "up", the crumb above.
 *
 *  A screen is not a URL. Opening a drawer, switching a tab or typing a filter stays on the screen; stepping to the next
 *  ticket is a move between siblings (same kind of thing under the same crumb). Neither counts as "where you came from":
 *  both hand the origin on, so the arrow jumps over them to the last screen that really was another one.
 *
 *  Kept in memory only, per history entry (React Router's location.key). A reload empties it and the arrow goes up, which is
 *  what the rest of the prototype does on a reload as well. */

const path = (href: string) => href.split(/[?#]/)[0]!;

export interface TrailEntry {
  /** React Router's key of the history entry */ key: string;
  /** its position in the tab's history; null when the router does not say */ idx: number | null;
  pathname: string; /** path and query, what to push when stepping back through history is not possible */ href: string;
  /** what the arrow calls this screen: "Search", "All open", "#4105" */ title: string;
  /** the crumb above it, null at the top of an area */ up: string | null;
  origin: Origin | null;
}
export interface Origin { href: string; title: string; idx: number | null; }

/** The origin of a screen that is being entered for the first time, given the screen that was showing until now. */
export function originOf(prev: Omit<TrailEntry, "key"> | null, next: { pathname: string; up: string | null }): Origin | null {
  if (!prev) return null;
  const sameScreen = prev.pathname === next.pathname;
  const sibling = !sameScreen && prev.up !== null && next.up !== null && path(prev.up) === path(next.up); // the list's filter may have changed in between: the path decides
  if (sameScreen || sibling) return prev.origin;
  return { href: prev.href, title: prev.title, idx: prev.idx };
}

/** How many history entries lie between here and the origin; null when that cannot be said, and then the origin is pushed instead.
 *  Stepping back through history is preferred: the screen comes back as it was left, scroll position included, and the
 *  browser's own back agrees with the arrow. */
export function stepsBack(origin: Origin, idx: number | null): number | null {
  if (origin.idx == null || idx == null) return null;
  const n = idx - origin.idx;
  return n >= 1 ? n : null;
}

/** The origin is the crumb above anyway (a ticket opened from its list): the arrow needs no words then. */
export const isUp = (origin: Origin, up: string | null, base = "") => up !== null && path(up) === `${base}${path(origin.href)}`;

/** What an area's column is scoped by: the project, and the period of the reports. The panel shows the same column only with the same scope. */
const SCOPE_KEYS = ["project", "range"] as const;
/** The area of a path inside a workspace: its first two segments ("/kriesi/settings/tags" → "/kriesi/settings"). */
const areaOf = (pathname: string) => pathname.split("/").slice(0, 3).join("/");

/** Whether the origin is the panel (`?views=1`) of the same area as the screen at `pathname` + `search`, scoped the same. Then the way back
 *  to the panel steps back through history to it; otherwise the panel is pushed with the screen's own scope (a period changed on a report). */
export function isPanelOf(origin: Pick<Origin, "href">, pathname: string, search: string): boolean {
  const o = new URL(origin.href, "http://x"), now = new URLSearchParams(search);
  return o.searchParams.get("views") === "1" && areaOf(o.pathname) === areaOf(pathname) && SCOPE_KEYS.every((k) => o.searchParams.get(k) === now.get(k));
}

/** The memory itself: one record per history entry, and the entry that is showing. */
export function createTrail() {
  const entries = new Map<string, TrailEntry>();
  let showing: TrailEntry | null = null;
  return {
    /** Called on every render of a screen; the first call for a history entry decides its origin, later ones only refresh what the screen is called. */
    /** `lateral`: reached through the phone's tab bar. A tab is a move sideways, not deeper: there is nothing to go back to. */
    visit(now: Omit<TrailEntry, "origin">, lateral = false): TrailEntry {
      const known = entries.get(now.key);
      // a history entry's index never changes: the first reading stands, and a later render that could not read it keeps it
      const entry: TrailEntry = { ...now, idx: known?.idx ?? now.idx, origin: known ? known.origin : lateral ? null : originOf(showing && showing.key !== now.key ? showing : null, now) };
      entries.set(now.key, entry);
      showing = entry;
      return entry;
    },
    clear() { entries.clear(); showing = null; },
  };
}
